
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { FriendWithStatus, toManualUserId } from '@/types';
import { useAuth } from '@/context/AuthContext';
import { fetchUserProfiles } from '@/hooks/useFriendshipUtils';
import { getManualPlayers } from '@/integrations/supabase/manualPlayerService';

/**
 * Hook for managing friendship state (accepted, pending, sent)
 */
export const useFriendshipState = () => {
  const [friends, setFriends] = useState<FriendWithStatus[]>([]);
  const [pendingRequests, setPendingRequests] = useState<FriendWithStatus[]>([]);
  const [sentRequests, setSentRequests] = useState<FriendWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  const fetchFriendships = async () => {
    if (!user) return;

    setIsLoading(true);

    try {
      const { data: friendships, error } = await supabase
        .from('friendships')
        .select('*')
        .or(`user_id.eq.${user.id},friend_id.eq.${user.id}`);

      if (error) throw error;

      // Get unique user IDs from friendships
      const userIds = new Set<string>();
      friendships?.forEach(friendship => {
        userIds.add(friendship.user_id);
        userIds.add(friendship.friend_id);
      });
      userIds.delete(user.id);

      const userMap = await fetchUserProfiles(Array.from(userIds));

      // Use Sets to prevent duplicate friends
      const uniqueAcceptedFriendIds = new Set<string>();
      const uniquePendingIds = new Set<string>();
      const uniqueSentIds = new Set<string>();

      const acceptedFriends: FriendWithStatus[] = [];
      const pending: FriendWithStatus[] = [];
      const sent: FriendWithStatus[] = [];

      friendships?.forEach(friendship => {
        const isRequester = friendship.user_id === user.id;
        const otherUserId = isRequester ? friendship.friend_id : friendship.user_id;

        // Skip if we've already processed this friend in this category
        if (friendship.status === 'accepted' && uniqueAcceptedFriendIds.has(otherUserId)) {
          return;
        }
        if (friendship.status === 'pending' && !isRequester && uniquePendingIds.has(otherUserId)) {
          return;
        }
        if (friendship.status === 'pending' && isRequester && uniqueSentIds.has(otherUserId)) {
          return;
        }

        let otherUser = userMap.get(otherUserId);
        if (!otherUser) {
          otherUser = {
            id: otherUserId,
            name: `User ${otherUserId.substring(0, 4)}`,
            email: null,
            imageUrl: null
          };
        }

        const friendWithStatus: FriendWithStatus = {
          ...otherUser,
          friendshipId: friendship.id,
          status: friendship.status as 'pending' | 'accepted',
          isRequester,
        };

        if (friendship.status === 'accepted') {
          uniqueAcceptedFriendIds.add(otherUserId);
          acceptedFriends.push(friendWithStatus);
        } else if (friendship.status === 'pending') {
          if (isRequester) {
            uniqueSentIds.add(otherUserId);
            sent.push(friendWithStatus);
          } else {
            uniquePendingIds.add(otherUserId);
            pending.push(friendWithStatus);
          }
        }
      });

      // Fetch manual players and merge into friends list
      const manualPlayersList = await getManualPlayers();
      const manualFriends: FriendWithStatus[] = manualPlayersList.map(mp => ({
        id: toManualUserId(mp.id),
        name: mp.name,
        email: mp.email,
        isManual: true,
        manualPlayerId: mp.id,
        status: 'accepted' as const,
      }));

      setFriends([...acceptedFriends, ...manualFriends]);
      setPendingRequests(pending);
      setSentRequests(sent);
    } catch (error) {
      console.error('Error fetching friendships:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchFriendships();
    }
  }, [user]);

  return {
    friends,
    pendingRequests,
    sentRequests,
    isLoading,
    refreshFriendships: fetchFriendships
  };
};
